import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  ShieldExclamationIcon,
  ForwardIcon,
  ArrowLeftIcon,
  FunnelIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ClockIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import {
  getCaseDocuments,
  updateCaseDocument,
  getDiscoveryCase,
  analyzeDocument,
} from '../services/api';

const KEYBOARD_SHORTCUTS = {
  r: 'responsive',
  n: 'non_responsive',
  p: 'privileged',
  s: 'skip',
  w: 'withhold',
  d: 'produce',
};

const STATUS_COLORS = {
  responsive: 'bg-green-100 text-green-800 border-green-300',
  non_responsive: 'bg-gray-100 text-gray-600 border-gray-300',
  partially_responsive: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  pending: 'bg-blue-50 text-blue-600 border-blue-200',
};

const PRIVILEGE_COLORS = {
  not_privileged: 'bg-gray-100 text-gray-600',
  attorney_client: 'bg-purple-100 text-purple-800',
  work_product: 'bg-indigo-100 text-indigo-800',
  deliberative: 'bg-amber-100 text-amber-800',
  other: 'bg-red-100 text-red-800',
};

export default function ReviewWorkflow() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseInfo, setCaseInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, responsive, privileged
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Load case and documents
  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [caseRes, docsRes] = await Promise.all([
          getDiscoveryCase(caseId),
          getCaseDocuments(caseId, { limit: 1000 }),
        ]);
        setCaseInfo(caseRes.case);
        setDocuments(docsRes.documents || []);
        setStats(docsRes.stats || {});
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    if (filter === 'all') return documents;
    if (filter === 'pending') return documents.filter(d => d.relevance_status === 'pending');
    if (filter === 'responsive') return documents.filter(d => d.relevance_status === 'responsive');
    if (filter === 'privileged') return documents.filter(d => d.privilege_status !== 'not_privileged' && d.privilege_status !== 'pending');
    return documents;
  }, [documents, filter]);

  const currentDoc = filteredDocs[currentIndex];
  const totalDocs = filteredDocs.length;

  // Keyboard shortcuts
  const handleKeyDown = useCallback(async (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (!currentDoc) return;

    const key = e.key.toLowerCase();

    if (key === 'arrowdown' || key === 'j') {
      e.preventDefault();
      setCurrentIndex(prev => Math.min(prev + 1, totalDocs - 1));
      return;
    }
    if (key === 'arrowup' || key === 'k') {
      e.preventDefault();
      setCurrentIndex(prev => Math.max(prev - 1, 0));
      return;
    }

    if (key === 'r') {
      await handleDecision('relevance_status', 'responsive');
    } else if (key === 'n') {
      await handleDecision('relevance_status', 'non_responsive');
    } else if (key === 'p') {
      await handleDecision('privilege_status', 'attorney_client');
    } else if (key === 'w') {
      await handleDecision('production_status', 'withhold');
    } else if (key === 'd') {
      await handleDecision('production_status', 'produce');
    } else if (key === 's') {
      // Skip — just advance
      setCurrentIndex(prev => Math.min(prev + 1, totalDocs - 1));
    } else if (key === '?') {
      setShowShortcuts(prev => !prev);
    }
  }, [currentDoc, currentIndex, totalDocs]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDecision = async (field, value) => {
    if (!currentDoc) return;
    try {
      const updates = { [field]: value, reviewed_by: 'analyst', reviewed_at: new Date().toISOString() };
      await updateCaseDocument(caseId, currentDoc.doc_id, updates);

      // Update local state
      setDocuments(prev => prev.map(d =>
        d.doc_id === currentDoc.doc_id ? { ...d, ...updates } : d
      ));

      // Auto-advance
      if (currentIndex < totalDocs - 1) {
        setCurrentIndex(prev => prev + 1);
      }
    } catch (err) {
      console.error('Failed to update document:', err);
    }
  };

  const handleAIAnalyze = async () => {
    if (!currentDoc) return;
    setAnalyzing(true);
    try {
      const result = await analyzeDocument(caseId, currentDoc.doc_id);
      // Update local state with AI results
      setDocuments(prev => prev.map(d =>
        d.doc_id === currentDoc.doc_id ? { ...d, ...result } : d
      ));
    } catch (err) {
      console.error('AI analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  // Progress calculation
  const reviewedCount = documents.filter(d => d.reviewed_by).length;
  const progress = documents.length > 0 ? (reviewedCount / documents.length) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/cases/${caseId}`)} className="text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Review</h1>
            <p className="text-sm text-gray-500">
              {caseInfo?.case_name || 'Case'} — {caseInfo?.case_number || ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowShortcuts(prev => !prev)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
          >
            <KeyIcon className="w-4 h-4" />
            Shortcuts
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
          <span>{reviewedCount} of {documents.length} reviewed</span>
          <span>{progress.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Keyboard shortcuts overlay */}
      {showShortcuts && (
        <div className="mb-4 bg-gray-800 text-white rounded-lg p-4 text-sm">
          <h3 className="font-semibold mb-2">Keyboard Shortcuts</h3>
          <div className="grid grid-cols-3 gap-2">
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">R</kbd> Responsive</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">N</kbd> Not Responsive</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">P</kbd> Privileged (ACP)</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">W</kbd> Withhold</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">D</kbd> Produce</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">S</kbd> Skip</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">↑/K</kbd> Previous</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">↓/J</kbd> Next</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-700 rounded">?</kbd> Toggle this</div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4">
        <FunnelIcon className="w-4 h-4 text-gray-400" />
        {['all', 'pending', 'responsive', 'privileged'].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setCurrentIndex(0); }}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filter === f
                ? 'bg-indigo-100 border-indigo-400 text-indigo-800'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {f === 'all' ? `All (${documents.length})` :
             f === 'pending' ? `Pending (${documents.filter(d => d.relevance_status === 'pending').length})` :
             f === 'responsive' ? `Responsive (${documents.filter(d => d.relevance_status === 'responsive').length})` :
             `Privileged (${documents.filter(d => d.privilege_status !== 'not_privileged' && d.privilege_status !== 'pending').length})`}
          </button>
        ))}
        <span className="ml-auto text-sm text-gray-500">
          {currentIndex + 1} / {totalDocs}
        </span>
      </div>

      {/* Main content: document list + detail */}
      <div className="grid grid-cols-12 gap-4">
        {/* Document list (left) */}
        <div className="col-span-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto">
            {filteredDocs.map((doc, idx) => (
              <div
                key={doc.doc_id}
                onClick={() => setCurrentIndex(idx)}
                className={`px-3 py-2 border-b border-gray-100 cursor-pointer transition-colors ${
                  idx === currentIndex ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 truncate">{doc.title}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  {doc.relevance_status !== 'pending' && (
                    <span className={`px-1.5 py-0.5 text-xs rounded ${STATUS_COLORS[doc.relevance_status] || STATUS_COLORS.pending}`}>
                      {doc.relevance_status?.replace('_', ' ')}
                    </span>
                  )}
                  {doc.privilege_status && doc.privilege_status !== 'pending' && doc.privilege_status !== 'not_privileged' && (
                    <span className={`px-1.5 py-0.5 text-xs rounded ${PRIVILEGE_COLORS[doc.privilege_status] || ''}`}>
                      {doc.privilege_status?.replace('_', ' ')}
                    </span>
                  )}
                  {doc.reviewed_by && (
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" />
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {doc.custodian && <span>{doc.custodian} · </span>}
                  {doc.file_type}
                </div>
              </div>
            ))}
            {filteredDocs.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <DocumentTextIcon className="w-8 h-8 mx-auto mb-2" />
                No documents match this filter.
              </div>
            )}
          </div>
        </div>

        {/* Document detail (right) */}
        <div className="col-span-8">
          {currentDoc ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              {/* Document header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{currentDoc.title}</h2>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    {currentDoc.custodian && <span>Custodian: {currentDoc.custodian}</span>}
                    {currentDoc.date_created && <span>Date: {currentDoc.date_created}</span>}
                    {currentDoc.file_type && <span>Type: {currentDoc.file_type}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {currentDoc.confidence > 0 && (
                    <span className="text-xs text-gray-500">
                      AI Confidence: {(currentDoc.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                  <button
                    onClick={handleAIAnalyze}
                    disabled={analyzing}
                    className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {analyzing ? 'Analyzing...' : 'AI Analyze'}
                  </button>
                </div>
              </div>

              {/* Current status badges */}
              <div className="flex items-center gap-2 mb-4">
                <span className={`px-2 py-1 text-xs rounded border ${STATUS_COLORS[currentDoc.relevance_status] || STATUS_COLORS.pending}`}>
                  {currentDoc.relevance_status?.replace('_', ' ') || 'pending'}
                </span>
                <span className={`px-2 py-1 text-xs rounded ${PRIVILEGE_COLORS[currentDoc.privilege_status] || PRIVILEGE_COLORS.not_privileged}`}>
                  {currentDoc.privilege_status?.replace('_', ' ') || 'pending'}
                </span>
                {currentDoc.production_status && currentDoc.production_status !== 'pending' && (
                  <span className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200">
                    {currentDoc.production_status.replace('_', ' ')}
                  </span>
                )}
                {currentDoc.bates_begin && (
                  <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 font-mono">
                    {currentDoc.bates_begin}
                  </span>
                )}
              </div>

              {/* Document content preview */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {currentDoc.content_preview || 'No content preview available.'}
                </p>
              </div>

              {/* AI reasoning */}
              {currentDoc.reasoning && (
                <div className="bg-purple-50 rounded-lg p-3 mb-4">
                  <h4 className="text-xs font-medium text-purple-700 mb-1">AI Analysis</h4>
                  <p className="text-sm text-purple-900">{currentDoc.reasoning}</p>
                </div>
              )}

              {/* Decision buttons */}
              <div className="space-y-3">
                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">Responsiveness</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDecision('relevance_status', 'responsive')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors flex items-center gap-1.5 ${
                        currentDoc.relevance_status === 'responsive'
                          ? 'bg-green-100 border-green-400 text-green-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-green-50'
                      }`}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      Responsive <kbd className="ml-1 text-xs opacity-50">R</kbd>
                    </button>
                    <button
                      onClick={() => handleDecision('relevance_status', 'non_responsive')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors flex items-center gap-1.5 ${
                        currentDoc.relevance_status === 'non_responsive'
                          ? 'bg-gray-200 border-gray-400 text-gray-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <XCircleIcon className="w-4 h-4" />
                      Not Responsive <kbd className="ml-1 text-xs opacity-50">N</kbd>
                    </button>
                    <button
                      onClick={() => handleDecision('relevance_status', 'partially_responsive')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                        currentDoc.relevance_status === 'partially_responsive'
                          ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-yellow-50'
                      }`}
                    >
                      Partial
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">Privilege</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDecision('privilege_status', 'attorney_client')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors flex items-center gap-1.5 ${
                        currentDoc.privilege_status === 'attorney_client'
                          ? 'bg-purple-100 border-purple-400 text-purple-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-purple-50'
                      }`}
                    >
                      <ShieldExclamationIcon className="w-4 h-4" />
                      ACP <kbd className="ml-1 text-xs opacity-50">P</kbd>
                    </button>
                    <button
                      onClick={() => handleDecision('privilege_status', 'work_product')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                        currentDoc.privilege_status === 'work_product'
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-indigo-50'
                      }`}
                    >
                      Work Product
                    </button>
                    <button
                      onClick={() => handleDecision('privilege_status', 'not_privileged')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                        currentDoc.privilege_status === 'not_privileged'
                          ? 'bg-gray-200 border-gray-400 text-gray-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Not Privileged
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-medium text-gray-500 mb-2">Production</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDecision('production_status', 'produce')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors flex items-center gap-1.5 ${
                        currentDoc.production_status === 'produce'
                          ? 'bg-green-100 border-green-400 text-green-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-green-50'
                      }`}
                    >
                      Produce <kbd className="ml-1 text-xs opacity-50">D</kbd>
                    </button>
                    <button
                      onClick={() => handleDecision('production_status', 'withhold')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors flex items-center gap-1.5 ${
                        currentDoc.production_status === 'withhold'
                          ? 'bg-red-100 border-red-400 text-red-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-red-50'
                      }`}
                    >
                      Withhold <kbd className="ml-1 text-xs opacity-50">W</kbd>
                    </button>
                    <button
                      onClick={() => handleDecision('production_status', 'produce_with_redactions')}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                        currentDoc.production_status === 'produce_with_redactions'
                          ? 'bg-amber-100 border-amber-400 text-amber-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-amber-50'
                      }`}
                    >
                      Produce w/ Redactions
                    </button>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentIndex(prev => Math.max(prev - 1, 0))}
                  disabled={currentIndex === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-30"
                >
                  ← Previous
                </button>
                <button
                  onClick={() => setCurrentIndex(prev => Math.min(prev + 1, totalDocs - 1))}
                  disabled={currentIndex >= totalDocs - 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-30 flex items-center gap-1"
                >
                  Next → <kbd className="text-xs opacity-50">S</kbd>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No document selected.</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats footer */}
      <div className="mt-6 grid grid-cols-6 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, color: 'text-gray-700' },
          { label: 'Reviewed', value: stats.reviewed || 0, color: 'text-blue-700' },
          { label: 'Responsive', value: stats.responsive || 0, color: 'text-green-700' },
          { label: 'Non-Responsive', value: stats.non_responsive || 0, color: 'text-gray-500' },
          { label: 'Privileged', value: stats.privileged || 0, color: 'text-purple-700' },
          { label: 'Produced', value: stats.produced || 0, color: 'text-indigo-700' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
